  import { useState } from "react";
  import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";

  // LAYOUT
  import MainLayout from "./layouts/MainLayout";
  import RequireModule from "./components/RequireModule";

  // LOGIN
  import Login from "./pages/Login";

  // DASHBOARD
  import Dashboard from "./pages/dashboard/Dashboard";

  // MRF
  import MRF from "./pages/mrf/MRF";
  import MRFList from "./pages/mrf/MRFList";

  // EXPENSES
  import SitePurchase from "./pages/purchase/SitePurchase";
  import ExpenseApproval from "./pages/expenses/ExpenseApproval";

  // SERVICES
  // NOTE: CreateService.js import removed — dead duplicate engine.
  // ServiceList.jsx has its own inline "Create Service" modal, which is the
  // only flow actually reachable from the UI ("+ New Service" button opens
  // the modal directly, it never navigates to /services/create). Keeping
  // both alive risked silent divergence (already happened once — the
  // client-site-filter fix had to be applied twice). Single engine now.
  import ServiceList      from "./pages/services/ServiceList";
  import ServiceDashboard from "./pages/services/ServiceDashboard";
  import ServiceDetail    from "./pages/services/ServiceDetail";
  import AMCContracts    from "./pages/services/AMCContracts";
  import OEMReturns      from "./pages/services/OEMReturns";

  // TRANSPORT
  import TransportLog from "./pages/transport/TransportLog";

  import CompanyProfile from "./pages/admin/CompanyProfile";
  import SuperAdmin     from "./pages/admin/SuperAdmin";
  import { CompanyProvider } from "./context/CompanyContext";

  // ACCOUNTS
  import Accounts from "./pages/accounts/Accounts";
  import PaymentEntry from "./pages/accounts/PaymentEntry";
  import ExpenseLedger from "./pages/accounts/ExpenseLedger";
  import IncomeLedger from "./pages/accounts/IncomeLedger";
  import CashBankBook from "./pages/accounts/CashBankBook";
  // VendorPayments retired (D-289) — vendors are paid on Payables (khaata)
  import TallyExport from "./pages/accounts/TallyExport";
  import OpeningBalances from "./pages/accounts/OpeningBalances";
    import Payables from "./pages/accounts/Payables";

  // BILLING
  import BillingDashboard from "./pages/billing/BillingDashboard";
  import CreateInvoice from "./pages/billing/CreateInvoice";
  import InvoiceList from "./pages/billing/InvoiceList";
  import InvoiceDetail from "./pages/billing/InvoiceDetail";
  import GRNDetail from "./pages/purchase/GRNDetail";
  import AwaitingBills from "./pages/purchase/AwaitingBills";
  import PurchaseBillForm from "./pages/purchase/PurchaseBillForm";
  import PurchaseBillView from "./pages/purchase/PurchaseBillView";
  import { BILL_ENTRY_ROLES, canDoAccounts } from "./services/purchaseBillService";
  import { PO_CREATE_ROLES, PO_GRN_ROLES, PO_LIST_ROLES, PO_VIEW_ROLES, hasPoRole } from "./utils/poRoles";

  // REPORTS
  import ReportingDashboard from "./pages/reports/ReportingDashboard";
  import FinancialReports from "./pages/reports/FinancialReports";
  import InventoryReports from "./pages/reports/InventoryReports";
  import ProjectReports from "./pages/reports/ProjectReports";

  // TECHNICIAN
  import TechnicianDashboard from "./pages/technician/TechnicianDashboard";
  import TaskDetail from "./pages/tasks/TaskDetail";

  // MACHINE
  import MachineList   from "./pages/machines/MachineList";
  import MachineForm   from "./pages/machines/MachineForm";
  import MachineDetail from "./pages/machines/MachineDetail";

  // STORE
  import MRFIssueDashboard from "./roles/store/MRFIssueDashboard";
  import StoreMRFDetail from "./roles/store/StoreMRFDetail";
  import MaterialSummary from "./roles/store/MaterialSummary";
  import PurchaseList from "./roles/store/PurchaseList";
  import ProcurementQueue from "./roles/store/ProcurementQueue";
  import ReceiveMaterial from "./roles/store/ReceiveMaterial";
  import InventoryLedger from "./roles/store/InventoryLedger";
  import ItemLedger from "./roles/store/ItemLedger";
  import StockList from "./roles/store/StockList";
  import InventoryDashboard from "./roles/store/InventoryDashboard";
  import ItemMaster from "./roles/store/ItemMaster";
  import DefectiveStockDashboard from "./roles/store/DefectiveStockDashboard";
  import ReturnMaterial from "./roles/store/ReturnMaterial";
  import ItemRequests   from "./roles/store/ItemRequests";
  import SparePartRequests from "./roles/store/SparePartRequests";
  import EquipmentRegistry from "./roles/store/EquipmentRegistry";
  import SiteStockView from "./roles/store/SiteStockView";
  import SiteTransfersPending from "./roles/store/SiteTransfersPending";
  import PurchaseOrderList from "./pages/purchase/PurchaseOrderList";
  import PurchaseOrderDetail from "./pages/purchase/PurchaseOrderDetail";
  import CreateDirectPO from "./pages/purchase/CreateDirectPO";
  import ConfirmSiteReceipt from "./pages/purchase/ConfirmSiteReceipt";
  import FinalizeSitePurchase from "./pages/purchase/FinalizeSitePurchase";
  import MySiteConfirmations from "./pages/purchase/MySiteConfirmations";

  // SUBCONTRACTOR
  import SubcontractorWork from "./pages/subcontractors/SubcontractorWork";

  // ENQUIRIES
  import EnquiryList   from "./pages/enquiries/EnquiryList";
  import EnquiryDetail from "./pages/enquiries/EnquiryDetail";

  // PROJECTS
  import ProjectList      from "./pages/projects/ProjectList";
  import ProjectCreate    from "./pages/projects/ProjectCreate";
  import ProjectDetail    from "./pages/projects/ProjectDetail";
  import ProjectChecklist from "./pages/projects/ProjectChecklist";
  import ProjectDocuments  from "./pages/projects/ProjectDocuments";
  import ProjectMaterials  from "./pages/projects/ProjectMaterials";

  // QUOTATIONS
  import QuotationList   from "./pages/quotations/QuotationList";
  import QuotationCreate from "./pages/quotations/QuotationCreate";
  import QuotationDetail from "./pages/quotations/QuotationDetail";

  // INVENTORY
  import DirectIssue from "./roles/store/DirectIssue";
  import DefectiveReturn from "./pages/inventory/DefectiveReturn";
  import StockAdjustment from "./pages/inventory/StockAdjustment";
  import StockAdjustmentApproval from "./pages/inventory/StockAdjustmentApproval";
  import VendorReturn from "./pages/inventory/VendorReturn";

  import VendorMaster from "./pages/Vendors/VendorMaster";
  import VendorList from "./pages/Vendors/VendorList";

  // CLIENT MASTER
  import ClientList from "./pages/master/ClientList";
  import ClientForm from "./pages/master/ClientForm";
  import EditClient from "./pages/master/EditClient";

  // ASSETS
  import AssetMaster from "./pages/assets/AssetMaster";
  import AssetLedger from "./pages/assets/AssetLedger";
  import TransferRequest from "./pages/assets/TransferRequest";
  import PendingTransfers from "./pages/assets/PendingTransfers";
  import AssetDetails from "./pages/assets/AssetDetails";
  import ReturnAssets from "./pages/assets/ReturnAssets";
  import ReceiveReturns from "./pages/assets/ReceiveReturns";

  // USER MANAGEMENT
  import UserManagement from "./pages/users/UserManagement";
  import AddUser from "./pages/users/AddUser";
  import EditUser from "./pages/users/EditUser";

  // ─────────────────────────────────────────────────────────────────────────────
  import { useEffect } from "react";
  import { setupOfflineSync } from "./services/offlineSyncService";

  function App() {
    const [canAccounts] = useState(() => canDoAccounts(JSON.parse(localStorage.getItem("user") || "{}"))); // D-314
    const [role] = useState(() => {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return user?.role || null;
    });

    useEffect(() => {
      setupOfflineSync();
    }, []);

    return (
      <CompanyProvider>
      <Router>
        <Routes>

          {/* ── LOGIN ── */}
          <Route
            path="/login"
            element={role ? <Navigate to="/" replace /> : <Login />}
          />

          {/* ── MAIN LAYOUT — auth guard ── */}
          <Route
            path="/"
            element={role ? <MainLayout /> : <Navigate to="/login" replace />}
          >

            {/* DASHBOARD */}
            <Route index element={<Dashboard />} />

            {/* ── ASSETS — unconditional (guarded internally) ── */}
            <Route path="assets"                element={<AssetMaster />} />
            <Route path="assets/ledger"         element={<AssetLedger />} />
            <Route path="assets/transfer"       element={<TransferRequest />} />
            <Route path="assets/pending"        element={<PendingTransfers />} />
            <Route path="assets/details"        element={<AssetDetails />} />
            <Route path="assets/returns"        element={<ReturnAssets />} />
            <Route path="assets/receive-returns" element={<ReceiveReturns />} /> 

            {(role === "sm" || role === "owner" || role === "manager" || role === "store") && (
    <Route path="transport" element={<TransportLog />} />
  )}

            {/* Subcontractor Work — Manager confirms/rejects bills raised
                by field roles (16 Sep 2026, two-stage flow). Site
                Engineers AND Technicians can log field observations;
                only sm/owner/manager can confirm a bill (financial
                gate) — enforced again inside SubcontractorWork.jsx and
                subcontractorService.js, this route gate is just the
                first layer. */}
            {(role === "sm" || role === "owner" || role === "manager" || role === "site_engineer" || role === "technician") && (
              <Route path="subcontractor-work" element={<SubcontractorWork />} />
            )}

            {/* ── PURCHASE ORDERS — HIGH-01 N1: each route gated by the shared PO role
                lists (src/utils/poRoles.js = firestore.rules); every other role falls to
                the catch-all → Home. receive/:id stays before :poId. */}
            {hasPoRole(PO_LIST_ROLES, role) && (
              <Route path="purchase-orders" element={<PurchaseOrderList />} />
            )}
            {hasPoRole(PO_CREATE_ROLES, role) && (
              <Route path="purchase-orders/create"       element={<CreateDirectPO />} />
            )}
            {hasPoRole(PO_GRN_ROLES, role) && (
              <Route path="purchase-orders/receive/:id"  element={<ReceiveMaterial />} />
            )}
            {hasPoRole(PO_VIEW_ROLES, role) && (
              <Route path="purchase-orders/:id/grn"      element={<GRNDetail />} />
            )}
                        {/* tally-export: Owner / Accounts only - in the ACCOUNTANT block below (this line had opened it to every role by URL; removed 28 Oct) */}

            {/* Direct Site Purchase (Priority 4) — Stage 1 confirm is
                Site Engineer's own gated route (added below, near their
                other routes). Stage 2 finalize is Manager/SM/Owner only. */}
            {(role === "sm" || role === "owner" || role === "manager") && (
              <Route path="purchase-orders/finalize-site/:id" element={<FinalizeSitePurchase />} />
            )}

            {hasPoRole(PO_VIEW_ROLES, role) && (
              <Route path="purchase-orders/:poId"        element={<PurchaseOrderDetail />} />
            )}
            {/* Accounts may view a PO but not create one - without this line
                /purchase-orders/create would match :poId ("Purchase order not found."). */}
            {hasPoRole(PO_VIEW_ROLES, role) && !hasPoRole(PO_CREATE_ROLES, role) && (
              <Route path="purchase-orders/create" element={<Navigate to="/" replace />} />
            )}

            {/* D-291 P3d-5a: vendor bills - same roles as purchaseBillService
                (BILL_ENTRY_ROLES) and firestore.rules canEnterBills(). */}
            {BILL_ENTRY_ROLES.includes(role) && (
              <>
                <Route path="awaiting-bills"                element={<AwaitingBills />} />
                <Route path="purchase-bills/new/:receiptId" element={<PurchaseBillForm />} />
                <Route path="purchase-bills/:billId"        element={<PurchaseBillView />} />
              </>
            )}

            {/* ── MRF ── */}
            {["technician", "sm", "owner", "store", "manager", "site_engineer"].includes(role) && (
    <Route path="mrf" element={<MRF />} />
  )}

            {/* ── EXPENSES ── */}
            {["technician", "sm", "owner", "store", "manager", "site_engineer"].includes(role) && (
    <Route path="expenses" element={<SitePurchase />} />
  )}

            {/* ── CLIENT MASTER ── */}
            {["owner", "manager"].includes(role) && (
              <>
                <Route path="master/client"          element={<ClientForm />} />
                <Route path="master/client-list"     element={<ClientList />} />
                <Route path="master/edit-client/:id" element={<EditClient />} />
              </>
            )}

            {/* ── TASKS ── */}
            {["technician", "owner", "sm"].includes(role) && (
              <>
                <Route path="tasks/:id" element={<TaskDetail />} />
                <Route path="machines/add" element={<MachineForm />} />
                <Route path="machines" element={<MachineList />} />
                <Route path="machines/:id" element={<MachineDetail />} />
              </>
            )}

            {/* ── TECHNICIAN DASHBOARD ── */}
            <Route
              path="technician"
              element={
                ["technician", "owner"].includes(role)
                  ? <TechnicianDashboard />
                  : <Navigate to="/" replace />
              }
            />

            {/* Site Machines — read access for Site Engineer, same list
                page store/manager already use. */}
            {role === "site_engineer" && (
              <Route path="machines" element={<MachineList />} />
            )}

            {/* ── TEST ── */}
            <Route path="test-tech" element={<div>TECH WORKING</div>} />

            {/* ── ENQUIRIES ── */}
            {(role === "sm" || role === "owner" || role === "manager") && (
              <>
                <Route path="enquiries"     element={<EnquiryList />} />
                <Route path="enquiries/:id" element={<EnquiryDetail />} />
              </>
            )}

            {/* — PROJECTS — */}
            {(role === "sm" || role === "owner" || role === "manager") && (
              <>
                <Route path="projects"                    element={<ProjectList />} />
                <Route path="projects/create"             element={<ProjectCreate />} />
                <Route path="projects/:id"                element={<ProjectDetail />} />
                <Route path="projects/:id/checklist"      element={<ProjectChecklist />} />
                <Route path="projects/:id/documents"      element={<ProjectDocuments />} />
                <Route path="projects/:id/materials"      element={<ProjectMaterials />} />
              </>
            )}

            {/* — PROJECTS (technician, read-scoped: list/detail/checklist only,
                no create/documents/materials — those stay manager-only) — */}
            {role === "technician" && (
              <>
                <Route path="projects"                    element={<ProjectList />} />
                <Route path="projects/:id"                element={<ProjectDetail />} />
                <Route path="projects/:id/checklist"      element={<ProjectChecklist />} />
              </>
            )}

            {/* — PROJECTS (site_engineer, read-scoped to assigned projects
                only — see projects.site_engineer_ids array, set by PM via
                ProjectDetail's Edit modal). Project create stays
                manager-only; documents/materials ARE allowed since Site
                Engineer directly executes on these projects. — */}
            {role === "site_engineer" && (
              <>
                <Route path="projects"                    element={<ProjectList />} />
                <Route path="projects/:id"                element={<ProjectDetail />} />
                <Route path="projects/:id/checklist"      element={<ProjectChecklist />} />
                <Route path="projects/:id/documents"      element={<ProjectDocuments />} />
                <Route path="projects/:id/materials"      element={<ProjectMaterials />} />
                <Route path="my-site-confirmations"            element={<MySiteConfirmations />} />
                <Route path="purchase-orders/confirm-site/:id" element={<ConfirmSiteReceipt />} />
              </>
            )}

            {/* ── QUOTATIONS ── */}
            {(role === "sm" || role === "owner" || role === "manager") && (
              <>
                <Route path="quotations"         element={<QuotationList />} />
                <Route path="quotations/create"  element={<QuotationCreate />} />
                <Route path="quotations/:id"     element={<QuotationDetail />} />
              </>
            )}

            {/* ── SM + OWNER ── */}
            {(role === "sm" || role === "owner" || role === "manager") && (
    <>
      <Route path="mrf-list"          element={<MRFList />} />
      <Route path="expense-approval"  element={<ExpenseApproval />} />
      {/* services/create route removed — ServiceList.jsx's inline modal
          is the single, governed Create Service flow (see D-XXX). */}
      <Route path="service-dashboard" element={<ServiceDashboard />} />
      <Route path="transport"         element={<TransportLog />} />
      <Route path="amc-contracts"     element={<AMCContracts />} />
      <Route path="oem-returns"       element={<OEMReturns />} />
      <Route path="machines"          element={<MachineList />} />
      <Route path="machines/add"      element={<MachineForm />} />
      <Route path="machines/edit/:id" element={<MachineForm />} />
      <Route path="machines/:id"      element={<MachineDetail />} />

    </>
  )}

            {/* ── SERVICE LIST — sm/owner/manager AND technician
                (MainLayout's nav item already shows this to technician too;
                the route must exist for that role or it silently
                redirects to Home via the catch-all "*" route) ── */}
            {["sm", "owner", "manager", "technician"].includes(role) && (
              <Route path="services" element={<ServiceList />} />
            )}

            {/* ── SERVICE DETAIL ── */}
            {["sm", "owner", "manager", "technician"].includes(role) && (
              <Route path="services/:id" element={<ServiceDetail />} />
            )}

            {/* ── D-314 ACCOUNTS ACCESS — a manager / sm the Owner gave "Accounts access":
                money pages only (no billing, Payment Entry, ledgers, Tally, reports).
                Owner / accounts get these same routes from the block below. ── */}
            {canAccounts && !["owner", "accounts"].includes(role) && (
              <>
                <Route path="accounts"         element={<Accounts />} />
                <Route path="cash-bank-book"   element={<CashBankBook />} />
                <Route path="payables"         element={<Payables />} />
                <Route path="opening-balances" element={<OpeningBalances />} />
              </>
            )}

            {/* ── ACCOUNTANT ── */}
            {(role === "accounts" || role === "owner") && (
              <>
                {/* ACCOUNTS */}
                <Route path="accounts"        element={<Accounts />} />
                <Route path="payment-entry"   element={<PaymentEntry />} />
                <Route path="expense-ledger"  element={<ExpenseLedger />} />
                <Route path="income-ledger"   element={<IncomeLedger />} />
                <Route path="cash-bank-book"  element={<CashBankBook />} />
                <Route path="vendor-payments" element={<Navigate to="/payables" replace />} />
                                <Route path="payables"        element={<Payables />} />
                                <Route path="opening-balances" element={<OpeningBalances />} />

                {/* BILLING */}
                <Route path="billing/dashboard"   element={<BillingDashboard />} />
                <Route path="billing/create"      element={<CreateInvoice />} />
                <Route path="billing/invoices"    element={<InvoiceList />} />
                <Route path="billing/invoice/:id" element={<InvoiceDetail />} />

                {/* TALLY + REPORTS */}
                <Route path="tally-export"      element={<TallyExport />} />
                <Route path="reports/financial" element={<RequireModule name="reports"><FinancialReports /></RequireModule>} />
                <Route path="reports/dashboard" element={<RequireModule name="reports"><ReportingDashboard /></RequireModule>} />
              </>
            )}

            {/* ── OWNER ONLY ── */}
            {role === "owner" && (
              <>
                {/* REPORTS */}
                <Route path="reports/inventory" element={<RequireModule name="reports"><InventoryReports /></RequireModule>} />
                <Route path="reports/projects"  element={<RequireModule name="reports"><ProjectReports /></RequireModule>} />
  
                {/* USER MANAGEMENT */}
                <Route path="users"           element={<UserManagement />} />
                <Route path="users/add"       element={<AddUser />} />
                <Route path="users/edit/:id"  element={<EditUser />} />
                <Route path="company-profile" element={<CompanyProfile />} />
              </>
            )}

            {/* ── STORE / INVENTORY (sm, manager, owner) ── */}
            {(role === "sm" || role === "manager" || role === "owner" || role === "store") && (
              <>
                <Route path="store-mrf"        element={<MRFIssueDashboard />} />
                <Route path="store-mrf/:id"    element={<StoreMRFDetail />} />
                <Route path="store-summary"    element={<MaterialSummary />} />
                <Route path="store-procurement" element={<ProcurementQueue />} />
                <Route path="purchase-list"    element={<PurchaseList />} />

                <Route path="item-master"      element={<ItemMaster />} />
                <Route path="vendor-master"    element={<VendorMaster />} />
                <Route path="vendor-list"      element={<VendorList />} />
                <Route path="defective-return" element={<DefectiveReturn />} />
                <Route path="stock-adjustment"          element={<StockAdjustment />} />
                <Route path="stock-adjustment-approval" element={<StockAdjustmentApproval />} />
                <Route path="vendor-return"    element={<VendorReturn />} />
                <Route path="stock-list"       element={<StockList />} />
                <Route path="inventory-ledger" element={<InventoryLedger />} />
                <Route path="item-ledger/:itemId" element={<ItemLedger />} />
                <Route path="inventory-dashboard" element={<InventoryDashboard />} />
                <Route path="defective-stock"  element={<DefectiveStockDashboard />} />
                <Route path="direct-issue"     element={<DirectIssue />} />
                <Route path="inventory-return"     element={<ReturnMaterial />} />
                <Route path="inventory-return/:id" element={<ReturnMaterial />} />
                <Route path="item-requests"        element={<ItemRequests />} />
                <Route path="spare-part-requests"  element={<SparePartRequests />} />
                <Route path="equipment-registry"   element={<EquipmentRegistry />} />
                <Route path="site-stock"           element={<SiteStockView />} />
                <Route path="site-transfers-pending" element={<SiteTransfersPending />} />
              </>
            )}

            {/* ── SUPER ADMIN — UID check inside component ── */}
            <Route path="super-admin" element={<SuperAdmin />} />

            {/* ── FALLBACK ── */}
            <Route path="*" element={<Navigate to="/" replace />} />

          </Route>
        </Routes>
    </Router>
      </CompanyProvider>
    );
  }

  export default App;